import { HelpMessage } from 'utils/constants'
import { CIMSColumn } from '../../../CIMSColumn/types'
import { Divider, Space, Spin } from 'antd'
import CIMSLink from 'components/CIMSLink'
import { alphabeticalSort } from 'shared'
import { CustomRowActionOptions } from '../../../CIMSTableContext/types/actions/row'
import SharePointExternalLink from './components/SharePointExternalLink'
import PacketLink from './components/PacketLink'
import {
  UseExternalLinksOptions,
  UseSharePointExternalLinks,
  UseSharePointExternalLinksOptions,
} from './types'
import { useCIMSModal } from 'components/CIMSModal/hooks/useCIMSModal'
import RepairModal from './components/RepairModal'
import { useGlobalState } from 'context/GlobalStateContext'
import {
  useOrphanedSharePointResourcesQuery,
  useRepairSharePointResourceMutation,
} from 'schema'
import {
  DefaultMutationHookOptions,
  DefaultQueryHookOptions,
} from 'utils/defaults'
import { useOptions } from 'components/CIMSSelect/hooks/useOptions'
import { simpleSuccessNotification } from 'hooks/notificationHandler'

/**
 * hook to handle the external links column in CIMSTables without a SharePoint
 * connection
 *
 * @see {@link UseExternalLinksOptions}
 */
export function useExternalLinks<T extends object>(
  options: UseExternalLinksOptions<T>
): CIMSColumn<T>
/**
 * hook to handle the external links column in CIMSTables with a SharePoint
 * connection
 *
 * @see {@link UseSharePointExternalLinksOptions}
 * @see {@link UseSharePointExternalLinks}
 */
export function useExternalLinks<T extends object>(
  options: UseSharePointExternalLinksOptions<T>
): UseSharePointExternalLinks<T>
export function useExternalLinks<T extends object>(
  options: UseExternalLinksOptions<T> | UseSharePointExternalLinksOptions<T>
): CIMSColumn<T> | UseSharePointExternalLinks<T> {
  const parentId = options.type === 'basic' ? undefined : options.parent.id
  const {
    state: { projectGlobalId: projectId },
  } = useGlobalState()
  const [visible, toggle, state] = useCIMSModal<T>()
  const { data, loading, refetch } = useOrphanedSharePointResourcesQuery({
    ...DefaultQueryHookOptions,
    skip: !parentId || !projectId,
    variables: {
      input: {
        projectId: projectId!,
        parent: { equals: parentId },
      },
    },
  })
  const [edit] = useRepairSharePointResourceMutation({
    ...DefaultMutationHookOptions,
    onCompleted: ({
      repairSharePointResource: {
        sharePointResource: { name },
      },
    }) => simpleSuccessNotification(`${name} succesfully repaired!`),
  })
  const repairOptions = useOptions({
    items: [
      ...(data?.orphanedSharePointResources?.concreteTestSetResources ?? []),
      ...(data?.orphanedSharePointResources
        ?.constructionAdminSubmittalResources ?? []),
      ...(data?.orphanedSharePointResources?.designSubmittalResources ?? []),
      ...(data?.orphanedSharePointResources?.fieldDesignChangeResources ?? []),
    ],
    value: 'id',
    label: 'name',
    sort: 'name',
  })
  if (options.type === 'basic') {
    const { dataFn } = options
    return {
      title: 'External Links',
      width: 200,
      help: HelpMessage.ExternalLinks({ search: true }),
      render: (record) => {
        const { externalLinks, prominentLinks } = dataFn(record)
        return (
          <Space direction="vertical" size="small" style={{ maxWidth: 200 }}>
            {prominentLinks &&
              prominentLinks
                .sort(alphabeticalSort('label'))
                .map(({ label, ...link }, i) => {
                  const key = `${Math.random()}-prominentLinks-${i}`
                  return link.external === false ? (
                    <CIMSLink key={key} {...link}>
                      {label}
                    </CIMSLink>
                  ) : (
                    <CIMSLink key={key} external href={link.href}>
                      {label}
                    </CIMSLink>
                  )
                })}
            {(prominentLinks?.length ?? 0) > 0 && externalLinks.length > 0 && (
              <Divider style={{ margin: 0 }} />
            )}
            {externalLinks
              .sort(alphabeticalSort('label'))
              .map(({ href, label }, i) => (
                <CIMSLink
                  key={`${Math.random()}-externalLinks-${i}`}
                  external
                  href={href}
                >
                  {label}
                </CIMSLink>
              ))}
          </Space>
        )
      },
    }
  }
  const {
    dataFn,
    parent,
    refetch: refetchRecords,
    loading: loadingProp,
  } = options
  const sharePointExternalLinks: CIMSColumn<T> = {
    title: 'External Links',
    width: 200,
    help: HelpMessage.ExternalLinks({ search: true }),
    render: (record) => {
      const { externalLinks, folder, packet, resource, prominentLinks } =
        dataFn(record)
      return (
        <Spin size="small" spinning={loadingProp ?? false}>
          <Space direction="vertical" size="small" style={{ maxWidth: 200 }}>
            {resource && <SharePointExternalLink {...resource} />}
            {packet && <PacketLink {...packet} />}
            {prominentLinks &&
              prominentLinks
                .sort(alphabeticalSort('label'))
                .map(({ label, ...link }, i) => {
                  const key = `${folder}-prominentLinks-${i}`
                  return link.external === false ? (
                    <CIMSLink key={key} {...link}>
                      {label}
                    </CIMSLink>
                  ) : (
                    <CIMSLink key={key} external href={link.href}>
                      {label}
                    </CIMSLink>
                  )
                })}
            {externalLinks.length > 0 && <Divider style={{ margin: 0 }} />}
            {externalLinks
              .sort(alphabeticalSort('label'))
              .map(({ href, label }, i) => (
                <CIMSLink
                  key={`${folder}-externalLinks-${i}`}
                  external
                  href={href}
                >
                  {label}
                </CIMSLink>
              ))}
          </Space>
        </Spin>
      )
    },
    filter: {
      type: 'search',
      with: 'callback',
      onFilter: (value, record) => {
        const {
          externalLinks,
          resource,
          folder,
          packet,
          prominentLinks = [],
        } = dataFn(record)
        const lower = value.toString().toLowerCase()
        const bySharePoint = !!resource && 'sharepoint folder'.includes(lower)
        const byParent = parent.name.toLowerCase().includes(lower)
        const byFolder = folder.toLowerCase().includes(lower)
        const byPacketName =
          packet?.name?.toLowerCase().includes(lower) ?? false
        const byPacketUrl =
          packet?.webUrl?.toLowerCase().includes(lower) ?? false
        const byLinks = [...externalLinks, ...prominentLinks].some((link) => {
          const href = 'href' in link ? link.href : undefined
          return (
            href?.toLowerCase().includes(lower) ||
            link.label.toLowerCase().includes(lower)
          )
        })
        return (
          bySharePoint ||
          byParent ||
          byFolder ||
          byLinks ||
          byPacketName ||
          byPacketUrl
        )
      },
    },
  }
  const repair: CustomRowActionOptions<T> = {
    type: 'button',
    hidden: (record) => !!dataFn(record).resource?.webUrl,
    text: 'Link Folder',
    loading,
    permission: true,
    onClick: (record) => toggle(record),
  }
  return {
    externalLinks: sharePointExternalLinks,
    repair,
    RepairModal: (
      <RepairModal
        folder={state ? dataFn(state).folder : 'unknown'}
        onOk={async (driveItemId) => {
          if (!state) return
          const { recordId } = dataFn(state)
          await edit({
            variables: {
              input: {
                parentId: parent.id,
                recordId: recordId.toString(),
                sharePointResourceId: driveItemId,
              },
            },
          })
          await refetchRecords()
          refetch()
        }}
        options={repairOptions}
        parent={parent}
        onClose={() => toggle(null)}
        visible={visible}
        state={state}
      />
    ),
  }
}
